import { Fragment } from "react";
import { images } from "../assets/styles/Images";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import moment from "moment/moment";

export default function ReceiptModal({ data, isOpen, setIsOpen }) {
  console.log("data", data);
  // Close Modal
  function closeModal() {
    setIsOpen(false);
  }

  // PDF styles
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#FFFFFF",
      padding: 30,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 20,
    },
    logo: {
      width: 120,
      height: 40,
    },
    companyInfoRight: {
      fontSize: 10,
      textAlign: "right",
    },
    companyInfoLeft: {
      fontSize: 10,
      textAlign: "left",
    },
    divider: {
      borderBottomWidth: 1,
      borderBottomColor: "#EEEEEE",
      marginVertical: 15,
    },
    borderContainer: {
      borderWidth: 1,
      borderColor: "#E5E7EB",
      borderRadius: 8,
      padding: 12,
      marginTop: 15,
    },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginVertical: 5,
    },
    threeColGrid: {
      flexDirection: "row",
      marginBottom: 20,
    },
    colLeft: {
      width: "33.33%",
    },
    colCenter: {
      width: "33.33%",
    },
    colRight: {
      width: "33.33%",
    },
    heading: {
      fontSize: 11,
      fontWeight: "bold",
      color: "#6B7280",
      marginBottom: 8,
      textAlign: "left",
    },
    headingRight: {
      fontSize: 11,
      fontWeight: "bold",
      color: "#6B7280",
      marginBottom: 8,
      textAlign: "right",
    },
    value: {
      fontSize: 11,
      marginBottom: 3,
      textAlign: "left",
    },
    valueSmall: {
      fontSize: 9,
      color: "#6B7280",
      marginBottom: 3,
      textAlign: "left",
    },
    valueCenter: {
      fontSize: 11,
      textAlign: "center",
    },
    valueRight: {
      fontSize: 11,
      textAlign: "right",
    },
    valueRightSmall: {
      fontSize: 9,
      color: "#6B7280",
      textAlign: "right",
    },
    totalValue: {
      fontSize: 14,
      fontWeight: "bold",
      textAlign: "right",
    },
    paidText: {
      fontSize: 10,
      color: "#10B981",
      fontWeight: "bold",
      textAlign: "right",
    },
    unpaidText: {
      fontSize: 10,
      color: "#dc2626",
      fontWeight: "bold",
      textAlign: "right",
    },
    tableHeader: {
      flexDirection: "row",
      borderTopWidth: 1,
      borderBottomWidth: 1,
      borderColor: "#E5E7EB",
      paddingVertical: 8,
    },
    tableHeaderText: {
      fontSize: 9,
      fontWeight: "bold",
      color: "#6B7280",
    },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#F3F4F6",
      paddingVertical: 8,
    },
    colDescription: {
      width: "40%",
      textAlign: "left",
    },
    colPrice: {
      width: "20%",
      textAlign: "right",
    },
    colDiscount: {
      width: "20%",
      textAlign: "right",
    },
    colAmount: {
      width: "20%",
      textAlign: "right",
    },
    subtotalContainer: {
      width: "100%",
      marginTop: 5,
      alignItems: "flex-end",
      marginBottom: 15,
    },
    subtotalWrapper: {
      width: "40%",
    },
    subtotalRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderBottomColor: "#F3F4F6",
    },
    totalRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingVertical: 6,
      fontWeight: "bold",
    },
    footerText: {
      fontSize: 9,
      fontWeight: "semibold",
      color: "#4F46E5",
      textAlign: "left",
      marginBottom: 5,
    },
  });

  // Create PDF Document Component
  const ReceiptDocument = (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Logo and Company Info */}
        <View>
          <Image src="https://i.ibb.co/9gTcV27/new-logo.png" style={styles.logo} />
        </View>
        <View style={{ flexDirection: "row", marginTop: 5, marginBottom: 15 }}>
          <View>
            <Text style={styles.companyInfoLeft}>www.edufeel.co.uk</Text>
            <Text style={styles.companyInfoLeft}>help@edufeel.com</Text>
            <Text style={styles.companyInfoLeft}>+44 123 213 000</Text>
          </View>
          <View style={{ flex: 1, alignItems: "flex-end" }}>
            <Text style={styles.companyInfoRight}>Edufeel Company</Text>
            <Text style={styles.companyInfoRight}>London, UK - 000 000</Text>
            <Text style={styles.companyInfoRight}>TAX ID 00000000123456XX</Text>
          </View>
        </View>

        {/* Main Content in Border */}
        <View style={styles.borderContainer}>
          {/* Billing Info Section */}
          <View style={styles.threeColGrid}>
            <View style={styles.colLeft}>
              <Text style={styles.heading}>Billed to</Text>
              <Text style={styles.value}>{data?.user?.nursery[0]?.name}</Text>
              <Text style={styles.valueSmall}>
                {data?.user?.nursery[0]?.address}, {data?.user?.nursery[0]?.country}
              </Text>
              <Text style={styles.valueSmall}>{data?.user?.nursery[0]?.phone_number}</Text>
            </View>
            <View style={styles.colCenter}>
              <Text style={styles.heading}>Invoice ID</Text>
              <Text style={styles.value}>{`#FHQ-${data?.transaction_id}`}</Text>
            </View>
            <View style={styles.colRight}>
              <Text style={styles.headingRight}>Invoice amount (GBP)</Text>
              <Text style={styles.totalValue}>£{data?.price}</Text>
              {data?.payment_status === "received" ? <Text style={styles.paidText}>PAID</Text> : <Text style={styles.unpaidText}>UNPAID</Text>}
            </View>
          </View>

          {/* Dates Section */}
          <View style={[styles.threeColGrid, { marginBottom: 15 }]}>
            <View style={styles.colLeft}>
              <Text style={styles.heading}>Billing Period</Text>
              <Text style={styles.value}>
                {moment.unix(data?.start_date).format("DD MMM, YYYY")} - {moment.unix(data?.end_date).format("DD MMM, YYYY")}
              </Text>
            </View>
            <View style={styles.colCenter}></View>
            <View style={styles.colRight}>
              <Text style={styles.headingRight}>Invoice date</Text>
              <Text style={styles.valueRight}>{moment.unix(data?.created_at_unix).format("DD MMM, YYYY")}</Text>
            </View>
          </View>

          {/* Table Section */}
          <View>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderText, styles.colDescription]}>DESCRIPTION</Text>
              <Text style={[styles.tableHeaderText, styles.colPrice]}>BILLING CYCLE</Text>
              <Text style={[styles.tableHeaderText, styles.colPrice]}>PRICE</Text>
              <Text style={[styles.tableHeaderText, styles.colPrice]}>SEATS</Text>
              <Text style={[styles.tableHeaderText, styles.colDiscount]}>TAX</Text>
              <Text style={[styles.tableHeaderText, styles.colAmount]}>AMOUNT (GBP)</Text>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.colDescription}>
                <Text style={{ fontSize: 11, fontWeight: "bold" }}>{data?.plan_name}</Text>
              </View>
              <Text style={[{ fontSize: 11 }, styles.colPrice]}>{data?.plan_type}</Text>
              <Text style={[{ fontSize: 11 }, styles.colPrice]}>£{data?.price}</Text>
              <Text style={[{ fontSize: 11 }, styles.colPrice]}>{data?.subscription?.max_user}</Text>
              <Text style={[{ fontSize: 11 }, styles.colDiscount]}>0%</Text>
              <Text style={[{ fontSize: 11 }, styles.colAmount]}>£{data?.price}</Text>
            </View>
          </View>

          {/* Subtotal Section */}
          <View style={styles.subtotalContainer}>
            <View style={styles.subtotalWrapper}>
              <View style={styles.subtotalRow}>
                <Text style={{ fontSize: 11 }}>Subtotal</Text>
                <Text style={{ fontSize: 11 }}>£{data?.price}</Text>
              </View>
              <View style={styles.subtotalRow}>
                <Text style={{ fontSize: 11 }}>Tax (0%)</Text>
                <Text style={{ fontSize: 11 }}>£0.00</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={{ fontSize: 11, fontWeight: "bold" }}>Total</Text>
                <Text style={{ fontSize: 11, fontWeight: "bold" }}>£{data?.price}</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-75"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-75"
              >
                <Dialog.Panel className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white p-8 text-center align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between ">
                    <Dialog.Title className="font-inter text-lg font-semibold text-gray-800">Receipt</Dialog.Title>
                    <XMarkIcon onClick={closeModal} className="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-700" />
                  </div>

                  <div className="pt-6">
                    <div className="w-44">
                      <img src={images.NewLogo} alt="receipt" className="h-full w-full object-contain" />
                    </div>
                    <div className="mb-6 mt-1 flex items-center">
                      <div>
                        <div className="text-left text-xs text-gray-500">
                          <div>www.edufeel.co.uk</div>
                          <div>help@edufeel.com</div>
                          <div>+44 123 213 000</div>
                        </div>
                      </div>
                      <div className="ml-auto text-right text-xs text-gray-500">
                        <div>Edufeel Company</div>
                        <div>London, UK - 000 000</div>
                        <div>TAX ID 00000000123456XX</div>
                      </div>
                    </div>
                    <div className="rounded-xl border p-3">
                      <div className="mb-8 grid grid-cols-3 gap-6">
                        <div>
                          <h4 className="mb-2 text-left text-xs font-medium text-gray-500">Billed to</h4>
                          <div className="text-sm">
                            <div className="text-left font-medium">{data?.user?.nursery[0]?.name}</div>
                            <div className="text-left text-xs text-gray-500">
                              {data?.user?.nursery[0]?.address}, {data?.user?.nursery[0]?.country}
                            </div>
                            <div className="text-left text-xs text-gray-500"> {data?.user?.nursery[0]?.phone_number}</div>
                          </div>
                        </div>
                        <div>
                          <h4 className="mb-2 text-xs font-medium text-gray-500">Invoice ID</h4>
                          <div className="text-sm font-medium">{`#FHQ-${data?.transaction_id}`}</div>
                        </div>
                        <div>
                          <h4 className="mb-2  text-right text-xs font-medium text-gray-500">Invoice amount (GBP)</h4>
                          <div className="text-right text-xl font-bold text-gray-800">£{data?.price}</div>
                          {data?.payment_status === "received" ? (
                            <div className="text-right text-xs font-medium text-green-600">PAID</div>
                          ) : (
                            <div className="text-right text-xs font-medium text-red-600">UNPAID</div>
                          )}
                        </div>
                      </div>
                      <div className="mb-6 grid grid-cols-3 gap-4">
                        <div>
                          <h4 className="mb-2 text-left text-xs font-medium text-gray-500">Billing Period</h4>
                          <div className="text-left text-xs font-medium">
                            {moment.unix(data?.start_date).format("DD MMM, YYYY")} - {moment.unix(data?.end_date).format("DD MMM, YYYY")}
                          </div>
                        </div>
                        {/* <div>
                          <h4 className="mb-2 text-xs font-medium text-gray-500">Next Billing Date</h4>
                          <div className="text-xs font-medium">15 Aug, 2024</div>
                        </div> */}
                        <div></div>
                        <div>
                          <h4 className="mb-2 text-right text-xs font-medium text-gray-500">Invoice date</h4>
                          <div className="text-right text-xs font-medium">{moment.unix(data?.created_at_unix).format("DD MMM, YYYY")}</div>
                        </div>
                      </div>
                      <div className="mb-6">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-y border-gray-200">
                              <th className="py-2 text-left text-xs font-medium text-gray-500">DESCRIPTION</th>
                              <th className="py-2 text-right text-xs font-medium text-gray-500">BILLING CYCLE</th>
                              <th className="py-2 text-right text-xs font-medium text-gray-500">PRICE</th>
                              <th className="py-2 text-right text-xs font-medium text-gray-500">SEATS</th>
                              <th className="py-2 text-right text-xs font-medium text-gray-500">TAX</th>
                              <th className="py-2 text-right text-xs font-medium text-gray-500">AMOUNT (GBP)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-2 text-left text-sm">
                                <div className="font-medium">{data?.plan_name}</div>
                              </td>
                              <td className="py-2 text-right text-sm">{data?.plan_type}</td>
                              <td className="py-2 text-right text-sm">£{data?.price}</td>
                              <td className="py-2 text-right text-sm">{data?.subscription?.max_user}</td>
                              <td className="py-2 text-right text-sm">0%</td>
                              <td className="py-2 text-right text-sm">£{data?.price}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="mb-6 -mt-4 flex justify-end">
                        <div className="w-60">
                          <div className="flex justify-between border-b border-gray-100 py-2 text-sm">
                            <span>Subtotal</span>
                            <span>£{data?.price}</span>
                          </div>
                          <div className="flex justify-between border-b border-gray-100 py-2 text-sm">
                            <span>Tax (0%)</span>
                            <span>£0.00</span>
                          </div>
                          <div className="flex justify-between py-2 text-sm font-medium">
                            <span>Total</span>
                            <span>£{data?.price}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-x-4 pt-4">
                    <PDFDownloadLink
                      document={ReceiptDocument}
                      fileName="receipt.pdf"
                      className={`inline-flex w-full justify-center rounded-full border border-secondary bg-secondary px-4 py-2.5 text-base font-medium text-white transition-all duration-300 hover:opacity-75 focus:outline-none`}
                    >
                      {({ blob, url, loading, error }) => (loading ? "Loading..." : "Download")}
                    </PDFDownloadLink>

                    <button
                      type="button"
                      className={`inline-flex w-full justify-center rounded-full border border-secondary bg-transparent px-4 py-2.5 text-base font-medium text-secondary transition-all duration-300 hover:bg-secondary hover:text-white focus:outline-none`}
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
